import { maxLength, required } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Strong, Text } from 'braid-design-system';
import React, {
  type ChangeEvent,
  type FocusEvent,
  forwardRef,
  type Ref,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { INPUT_MAX_LENGTH_DEFAULT } from 'src/app/shared/constants/formDataFields';
import {
  JOB_AD_REFERENCE_CLICKED,
  JOB_AD_REFERENCE_HELP_OBJECT_INTERACTION,
  JOB_AD_REFERENCE_HELP_TEXT_EXPAND,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { validateField } from 'src/app/utils/validation';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateJobAdReference } from 'src/app/v1/state/modules/formData/jobAdReference';
import { ContextualHelp } from 'src/app/v1/views/shared/ContextualHelp';
import { TextFieldWithCounter } from 'src/app/v1/views/shared/TextFieldWithCounter';
import type { AppState } from 'src/types/appState';
import type { JobAdReferenceRef } from 'src/types/formFields';

import translations from './.vocab';

interface Props {
  isRequired?: boolean;
}

export const JobAdReference = forwardRef(
  ({ isRequired = false }: Props, ref: Ref<JobAdReferenceRef>) => {
    const { t } = useTranslations(translations);
    const { getLanguage } = useConfig();

    const { jobAdReference, isLinkOutApplyViaSeek } = useSelector(
      (state: AppState) => ({
        jobAdReference: state.formData.jobAdReference,
        isLinkOutApplyViaSeek: state.tealium.isLinkOutApplyViaSeek,
      }),
    );

    const dispatch = useDispatch();

    const [validationMessage, setValidationMessage] = useState<string | null>(
      null,
    );
    const [inputValue, setInputValue] = useState(jobAdReference);

    useImperativeHandle(ref, () => ({
      handleValidation: (skipIfNull = false) =>
        handleInputValidation(inputValue, skipIfNull),
    }));

    const handleInputValidation = (
      value: string,
      skipIfNull = false,
    ): string | null => {
      const validationResult = validateField(
        {
          field: 'jobAdReference',
          validators: isRequired ? [required, maxLength] : [maxLength],
          validatorProps: {
            max: INPUT_MAX_LENGTH_DEFAULT,
            requiredMessage: t('job-ad-reference-validation-required'),
          },
          value,
        },
        skipIfNull,
        getLanguage(),
      );

      setValidationMessage(validationResult);

      return validationResult;
    };

    const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
      const eventData = {
        eventName: JOB_AD_REFERENCE_CLICKED,
        objectInteraction: 'job-reference',
        jobIsLinkOut: Boolean(isLinkOutApplyViaSeek),
      };
      getTrackingAdapter.sendLinkEvent(eventData);

      const { value } = event.target;
      const errorMessage = handleInputValidation(value);

      if (!errorMessage) {
        dispatch(actionUpdateJobAdReference(value));
      }
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setInputValue(value);
      handleInputValidation(value);
    };

    return (
      <TextFieldWithCounter
        id="JobAdReferenceTextField"
        label={
          <>
            {t('job-ad-reference-label')}{' '}
            <ContextualHelp
              ariaLabel="Job Ad Reference Help"
              tooltipId="job-ad-reference-help-tooltip"
              message={
                <Text>
                  {t('job-ad-reference-help-text', {
                    Strong: (children) => <Strong>{children}</Strong>,
                    Break: () => <br />,
                  })}
                </Text>
              }
              onTrigger={() =>
                getTrackingAdapter.sendLinkEvent({
                  eventName: JOB_AD_REFERENCE_HELP_TEXT_EXPAND,
                  objectInteraction: JOB_AD_REFERENCE_HELP_OBJECT_INTERACTION,
                })
              }
            />
          </>
        }
        secondaryLabel={
          !isRequired ? t('job-ad-reference-secondary-text') : undefined
        }
        value={inputValue}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        isValid={!Boolean(validationMessage)}
        message={validationMessage}
        maxCharacters={INPUT_MAX_LENGTH_DEFAULT}
        data={{
          'hj-whitelist': 'true',
          testid: 'JobAdReferenceTextField',
        }}
      />
    );
  },
);
